import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Scale, FileText, TrendingUp, LogOut, Lock, FileSearch, 
  BarChart3, ChevronDown, ChevronRight, CreditCard, Calendar, Clock, Briefcase,
  Settings, User, Receipt, FileCheck, Building2
} from 'lucide-react';
import axios from 'axios';
import { useBranding } from '../lib/BrandingContext';
import ModuleWalletDropdown from '../components/ModuleWalletDropdown';
import BuyCreditsModal from '../components/BuyCreditsModal';
import { WorkflowReport } from '../components/WorkflowTimer';
import AdvocateQueue from '../components/AdvocateQueue';

const TenantDashboard = () => {
  const navigate = useNavigate();
  const { branding } = useBranding();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBuyCredits, setShowBuyCredits] = useState(false);
  const [featureToggles, setFeatureToggles] = useState({
    create_legal_opinion: true,
    ec_extractor: true,
    institution_checklist: true,
    missing_doc_identifier: true,
    cs_dashboard: true,
    legal_opinion_validator: true,
    online_builder_opinion: true
  });
  const [expandedSections, setExpandedSections] = useState({
    analytics: false,
    applications: false
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (!token || !userData) {
      navigate('/login');
      return;
    }

    setUser(JSON.parse(userData));
    fetchStats();
    fetchFeatureToggles();
  }, [navigate]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/stats`);
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchFeatureToggles = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/feature-toggles`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      // Handle both formats - direct toggles or {toggles: {...}} wrapper
      const toggles = response.data.toggles || response.data;
      setFeatureToggles(toggles);
    } catch (err) {
      console.error('Error fetching feature toggles:', err);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-40">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {branding?.logo_url ? (
                <img src={branding.logo_url} alt={branding.company_name || 'Logo'} className="h-10" />
              ) : (
                <div className="flex items-center space-x-2">
                  <Scale className="w-8 h-8 text-blue-600" />
                  <span className="text-xl font-bold text-gray-900">
                    {branding?.company_name || 'TechOrange-Legal'}
                  </span>
                </div>
              )}
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{user?.full_name}</p>
                <p className="text-xs text-gray-500">@{user?.username}</p>
                {user?.role && (
                  <p className="text-xs text-blue-600 font-medium capitalize">{user.role.replace('_', ' ')}</p>
                )}
              </div>
              
              <ModuleWalletDropdown />
              
              <button
                onClick={() => navigate('/password-management')}
                className="flex items-center space-x-2 px-4 py-2 text-sm bg-gray-600 text-white hover:bg-gray-700 rounded-lg transition-colors"
                title="Password Management"
              >
                <Lock className="w-4 h-4" />
                <span>Password</span>
              </button>
              
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Left Sidebar */}
        <aside className="w-64 bg-gray-100 border-r border-gray-200 min-h-screen overflow-y-auto">
          <nav className="p-4 space-y-2">
            {/* My Account Section - Always visible at top */}
            <div className="mb-4 pb-4 border-b border-gray-300">
              <div className="px-4 py-2 text-gray-700 font-semibold flex items-center space-x-2">
                <User className="w-4 h-4" />
                <span>My Account</span>
              </div>
              <button
                onClick={() => navigate('/tenant-profile')}
                className="w-full text-left px-4 py-2 text-sm text-purple-600 hover:bg-purple-50 rounded font-medium flex items-center"
                data-testid="sidebar-tenant-profile"
              >
                <Building2 className="w-4 h-4 mr-2" />
                Tenant Profile
              </button>
              <button
                onClick={() => navigate('/settings/billing-profile')}
                className="w-full text-left px-4 py-2 text-sm text-green-600 hover:bg-green-50 rounded font-medium flex items-center"
              >
                <Receipt className="w-4 h-4 mr-2" />
                Billing & GST
              </button>
              <button
                onClick={() => navigate('/invoices')}
                className="w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded font-medium flex items-center"
              >
                <FileCheck className="w-4 h-4 mr-2" />
                My Invoices
              </button>
            </div>

            {/* Home/Analytics Section */}
            <div>
              <button
                onClick={() => toggleSection('analytics')}
                className="w-full flex items-center justify-between px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <span className="font-semibold">Home/Analytics</span>
                {expandedSections.analytics ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
              {expandedSections.analytics && (
                <div className="ml-4 mt-2 space-y-1">
                  <button
                    onClick={() => navigate('/chats')}
                    className="w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded font-medium"
                  >
                    💬 Messages
                  </button>
                  <button
                    onClick={() => navigate('/audit-logs')}
                    className="w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded font-medium"
                  >
                    📋 Audit Logs
                  </button>
                  <div className="px-4 py-2 text-sm text-gray-600 cursor-pointer hover:bg-gray-50 rounded">
                    Legal Opinions Generated
                  </div>
                  <div className="px-4 py-2 text-sm text-gray-600 cursor-pointer hover:bg-gray-50 rounded">
                    EC Extractions Generated
                  </div>
                  <div className="px-4 py-2 text-sm text-gray-600 cursor-pointer hover:bg-gray-50 rounded">
                    Bank Format Distribution
                  </div>
                </div>
              )}
            </div>

            {/* Applications Section */}
            <div>
              <button
                onClick={() => toggleSection('applications')}
                className="w-full flex items-center justify-between px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <span className="font-semibold">Applications</span>
                {expandedSections.applications ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
              {expandedSections.applications && (
                <div className="ml-4 mt-2 space-y-1">
                  {featureToggles.create_legal_opinion && (
                    <button
                      onClick={() => navigate('/extract')}
                      className="w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded"
                    >
                      Create Legal Opinion
                    </button>
                  )}
                  {featureToggles.online_builder_opinion && (
                    <button
                      onClick={() => navigate('/online-builder-opinion')}
                      className="w-full text-left px-4 py-2 text-sm text-blue-700 hover:bg-blue-50 rounded font-medium flex items-center"
                      data-testid="online-builder-opinion-nav"
                    >
                      <Briefcase className="w-4 h-4 mr-2" />
                      Online Builder Opinion
                    </button>
                  )}
                  {featureToggles.ec_extractor && (
                    <button
                      onClick={() => navigate('/ec-extractor')}
                      className="w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded"
                    >
                      EC Extractor
                    </button>
                  )}
                  {featureToggles.missing_doc_identifier && (
                    <button
                      onClick={() => navigate('/missing-docs')}
                      className="w-full text-left px-4 py-2 text-sm text-amber-600 hover:bg-amber-50 rounded font-medium"
                    >
                      📋 Missing Doc Identifier
                    </button>
                  )}
                  <button
                    onClick={() => navigate('/review')}
                    className="w-full text-left px-4 py-2 text-sm text-orange-600 hover:bg-orange-50 rounded font-medium"
                  >
                    ✅ Review Opinions
                  </button>
                  <button
                    onClick={() => navigate('/my-opinions')}
                    className="w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded"
                  >
                    My Opinions
                  </button>
                </div>
              )}
            </div>

            {/* Settings Section */}
            <div>
              <div className="px-4 py-2 text-gray-700 font-semibold flex items-center space-x-2">
                <Settings className="w-4 h-4" />
                <span>Settings</span>
              </div>
              <button
                onClick={() => navigate('/tenant-profile')}
                className="w-full text-left px-8 py-2 text-sm text-purple-600 hover:bg-purple-50 rounded font-medium"
                data-testid="tenant-profile-link"
              >
                <Building2 className="w-4 h-4 inline mr-2" />
                Tenant Profile
              </button>
              <button
                onClick={() => navigate('/settings/advocate-profile')}
                className="w-full text-left px-8 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded font-medium"
              >
                <User className="w-4 h-4 inline mr-2" />
                Advocate Profile
              </button>
              <button
                onClick={() => navigate('/settings/billing-profile')}
                className="w-full text-left px-8 py-2 text-sm text-green-600 hover:bg-green-50 rounded font-medium"
                data-testid="billing-profile-link"
              >
                <Receipt className="w-4 h-4 inline mr-2" />
                Billing Profile
              </button>
              <button
                onClick={() => navigate('/invoices')}
                className="w-full text-left px-8 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded font-medium"
                data-testid="invoice-history-link"
              >
                <FileCheck className="w-4 h-4 inline mr-2" />
                Invoice History
              </button>
            </div>

            {/* Security Section */}
            <div>
              <div className="px-4 py-2 text-gray-700 font-semibold">Security</div>
              <button
                onClick={() => navigate('/password-management')}
                className="w-full text-left px-8 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded"
              >
                Password
              </button>
            </div>

            {/* Actions Section */}
            <div>
              <div className="px-4 py-2 text-gray-700 font-semibold">Actions</div>
              <button
                onClick={() => navigate('/review')}
                className="w-full text-left px-8 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded"
              >
                Review Opinions
              </button>
            </div>
          </nav>
        </aside>

        {/* Main Content Area */}
        <main className="flex-1 p-8">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Dashboard Overview</h2>

            {/* Stats Grid - 3 columns */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Current Month Stats */}
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-700">Current Month</h3>
                  <Calendar className="w-5 h-5 text-blue-600" />
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-600">Legal Opinions</p>
                    <p className="text-2xl font-bold text-gray-900">{stats?.current_month_opinions || 0}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">EC Extractions</p>
                    <p className="text-2xl font-bold text-gray-900">{stats?.current_month_ec || 0}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">APF Opinions</p>
                    <p className="text-2xl font-bold text-gray-900">{stats?.current_month_apf || 0}</p>
                  </div>
                </div>
              </div>

              {/* Monthly Average */}
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-700">Monthly Average</h3>
                  <BarChart3 className="w-5 h-5 text-green-600" />
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-600">Avg Opinions/Month</p>
                    <p className="text-2xl font-bold text-gray-900">{stats?.avg_opinions_per_month || 0}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Avg EC/Month</p>
                    <p className="text-2xl font-bold text-gray-900">{stats?.avg_ec_per_month || 0}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Avg APF/Month</p>
                    <p className="text-2xl font-bold text-gray-900">{stats?.avg_apf_per_month || 0}</p>
                  </div>
                </div>
              </div>

              {/* Total Till Date */}
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-700">Total Till Date</h3>
                  <TrendingUp className="w-5 h-5 text-purple-600" />
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-600">Total Opinions</p>
                    <p className="text-2xl font-bold text-gray-900">{stats?.total_opinions || 0}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Total EC</p>
                    <p className="text-2xl font-bold text-gray-900">{stats?.total_ec || 0}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Total APF</p>
                    <p className="text-2xl font-bold text-gray-900">{stats?.total_apf || 0}</p>
                  </div>
                </div>
              </div>

              {/* Credit Usage - Current Month */}
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl shadow-sm p-6 border border-blue-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-blue-900">Current Month Credits</h3>
                  <CreditCard className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-blue-700">Credits Spent</p>
                  <p className="text-3xl font-bold text-blue-900">{stats?.current_month_credits || 0}</p>
                  <p className="text-xs text-blue-600 mt-1">This month&apos;s usage</p>
                </div>
              </div>

              {/* Credit Usage - Monthly Average */}
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl shadow-sm p-6 border border-green-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-green-900">Monthly Credits</h3>
                  <CreditCard className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-green-700">Avg Credits Spent</p>
                  <p className="text-3xl font-bold text-green-900">{stats?.avg_credits_per_month || 0}</p>
                  <p className="text-xs text-green-600 mt-1">Average per month</p>
                </div>
              </div>

              {/* Credit Usage - Total */}
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl shadow-sm p-6 border border-purple-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-purple-900">Total Credits</h3>
                  <CreditCard className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-purple-700">Total Spent Till Date</p>
                  <p className="text-3xl font-bold text-purple-900">{stats?.total_credits_spent || 0}</p>
                  <p className="text-xs text-purple-600 mt-1">Lifetime usage</p>
                </div>
              </div>
            </div>

            {/* Workflow Timing Report */}
            <div className="mt-8 bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-blue-600" />
                  📊 Workflow Timing Report
                </h3>
                <span className="text-sm text-gray-500">Individual case timings</span>
              </div>
              <WorkflowReport days={30} limit={15} tenantId={user?.tenant_id} />
            </div>

            {/* Advocate Queue - Cases Ready for Opinion */}
            <div className="mt-8 bg-white rounded-xl shadow-sm p-6 border border-gray-100" data-testid="advocate-queue-section">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-amber-600" />
                  📋 Cases Ready for Opinion
                </h3>
                <button
                  onClick={() => navigate('/missing-docs')}
                  className="text-sm text-blue-600 hover:underline"
                >
                  + New Case
                </button>
              </div>
              <AdvocateQueue onOpenCase={(caseId) => {
                // Navigate to case or show notification
                alert(`Case ${caseId} opened. You can now proceed with opinion generation.`);
              }} />
            </div>
          </div>
        </main>
      </div>

      {/* Buy Credits Modal */}
      <BuyCreditsModal open={showBuyCredits} onClose={() => setShowBuyCredits(false)} />
    </div>
  );
};

export default TenantDashboard;
